// FILES surface (N-X-3), recreated 1:1 from the prototype (screens.js files): the sticky toolbar carries
// the upload affordance + info; the four sub-tabs split the durable store's three kinds (all / reports &
// outputs / databases / uploads); the body is the prototype's artifact table over the composed listing the
// owning module (I) exposes — each row the module's DENY-BY-DEFAULT render decision (an inline File opens,
// a download-only File only downloads — G-031), the SANITISED name (G-032), and a composed origin label
// (never a raw id — G-197). The upload flow is part of a later stage — the affordance states that honestly
// (the dashboard writes no bytes here; toast copy states intended behaviour, README). Thin over services;
// tokens only (G-181).

import { useState } from 'react';
import { useSurfaceApi } from './SurfaceApiContext.tsx';
import { useProjection } from './useProjection.ts';
import { SurfaceFrame } from './surface-frame.tsx';
import { FileTable } from './components/FileTable.tsx';
import { Btn } from './primitives.tsx';
import { ScreenToolbar, ScreenBody, ScreenSubTabs, TbInfo } from '../app/screen-chrome.tsx';
import { Icon } from '../app/Icon.tsx';
import { useShell } from '../app/ShellContext.tsx';
import type { FilesProjection, FileViewRow } from './know-projections.ts';

/** The Files surface tabs — the prototype's four (the store's three kinds + all). */
type FilesTab = 'all' | 'out' | 'db' | 'up';

/** The rows one tab shows. */
function tabRows(rows: readonly FileViewRow[], tab: FilesTab): readonly FileViewRow[] {
  if (tab === 'all') return rows;
  if (tab === 'out') return rows.filter((row) => row.kind === 'report');
  if (tab === 'db') return rows.filter((row) => row.kind === 'database');
  return rows.filter((row) => row.kind === 'upload');
}

/** The per-tab footnote/card the prototype hangs under the table (each states a real posture). */
function TabNote({ tab }: { readonly tab: FilesTab }): React.JSX.Element | null {
  if (tab === 'out') {
    return (
      <p className="files-note">
        Outputs are point-in-time artifacts Ritona produces. Append-only by the agent — it writes
        and versions, <b>never hard-deletes</b>; retiring one is a human call.
      </p>
    );
  }
  if (tab === 'db') {
    return (
      <div className="files-card">
        <div className="h-section" style={{ fontSize: 'var(--text-md)' }}>
          Databases are living, not snapshots
        </div>
        <p>
          A &ldquo;database&rdquo; here is a structure Ritona set up and keeps current as work
          happens, with its own version history. Reports are point-in-time; these update
          continuously.
        </p>
      </div>
    );
  }
  if (tab === 'up') {
    return (
      <p className="files-note">
        Uploaded documents are <b>read-only</b> to Ritona by default — nothing it writes can
        overwrite your source file.
      </p>
    );
  }
  return null;
}

/** The Files surface. */
export function FilesSurface(): React.JSX.Element {
  const api = useSurfaceApi();
  const shell = useShell();
  const state = useProjection<FilesProjection>(api.getFiles);
  const [tab, setTab] = useState<FilesTab>('all');

  return (
    <>
      <ScreenToolbar title="Files">
        <Btn
          kind="ghost"
          sm
          onClick={() =>
            shell.showToast(
              'Upload a file — it lands as a read-only document Ritona can read, never overwrite. The upload flow wires in a later stage.',
            )
          }
        >
          <Icon name="upload" size={14} /> Upload a file
        </Btn>
        <TbInfo
          title="Files"
          tip="The durable artifact store. Three kinds live here: outputs Ritona produces, the live databases it stands up and keeps current, and documents you upload for it to read."
        />
      </ScreenToolbar>
      <ScreenSubTabs
        label="Files sections"
        tabs={[
          { id: 'all', label: 'All', badge: state.data?.rows.length },
          { id: 'out', label: 'Reports & outputs' },
          { id: 'db', label: 'Databases' },
          { id: 'up', label: 'Uploads' },
        ]}
        active={tab}
        onSelect={(id) => setTab(id as FilesTab)}
      />
      <ScreenBody>
        <SurfaceFrame state={state}>
          {(view) => (
            <section aria-label="Files">
              <FileTable rows={tabRows(view.rows, tab)} />
              <TabNote tab={tab} />
            </section>
          )}
        </SurfaceFrame>
      </ScreenBody>
    </>
  );
}
